import { useEffect, useState } from "react";

import { desktopBlockStyle } from "../styles";

import { names, thanks, photoCounts } from "../texts/personal";

interface ImageBlockProps extends PersonalProps {
  containerHeight: number;
  isTextBlock: boolean;
}

const createImageArray = (
  isMobile: boolean,
  isVertical: boolean,
  username: string
) => {
  const isDoubleWidget = (isMobile && !isVertical) || (!isMobile && isVertical);

  let photoArray: string[][] = [];
  let previousNumber = 0;
  let isPreviousVertical = false;

  Array.from({ length: photoCounts[username] }).forEach((_, index) => {
    const photoNumber = index + 1;
    if (!isDoubleWidget) {
      photoArray.push([`/thanks/${username}/${photoNumber}.jpg`]);
    } else if (isPreviousVertical) {
      photoArray.push([
        `/thanks/${username}/${previousNumber}.jpg`,
        `/thanks/${username}/${photoNumber}.jpg`,
      ]);
      previousNumber = 0;
      isPreviousVertical = false;
    } else {
      previousNumber = photoNumber;
      isPreviousVertical = true;
    }
  });

  if (previousNumber) {
    photoArray.push([`/thanks/${username}/${previousNumber}.jpg`]);
  }

  return photoArray;
};

const ImageBlock = ({
  isMobile,
  isVertical,
  isDark,
  username,
  setContent,
  setModalOpen,
  containerHeight,
  isTextBlock,
}: ImageBlockProps) => {
  const [photoNumber, setPhotoNumber] = useState(0);

  const images = createImageArray(isMobile, isVertical, username);

  const desktopWidht =
    window.innerWidth <= 1024
      ? 320
      : window.innerWidth < 1280
      ? 350
      : window.innerWidth < 1536
      ? 370
      : 450;

  const leftArrowButton = (
    <button
      className={
        photoNumber > 0
          ? `${isMobile ? "p-1" : "p-2"} ${
              isDark ? "bg-gray-700" : " bg-red-200"
            } 
          rounded-full hover:bg-wine`
          : "invisible"
      }
      onClick={() => setPhotoNumber(photoNumber - 1)}
    >
      <img src="/thanks/left-arrow.png" alt="right-arrow" width={30} />
    </button>
  );

  const rightArrowButton = (
    <button
      className={
        photoNumber < images.length - 1
          ? `${isMobile ? "p-1" : "p-2"} ${
              isDark ? "bg-gray-700" : " bg-red-200"
            }  rounded-full hover:bg-wine`
          : "invisible"
      }
      onClick={() => setPhotoNumber(photoNumber + 1)}
    >
      <img src="/thanks/right-arrow.png" alt="right-arrow" width={30} />
    </button>
  );

  useEffect(() => {
    if (images.length <= photoNumber) {
      setPhotoNumber(images.length - 1);
    }
  }, [images, photoNumber, setPhotoNumber]);

  return (
    <div
      className={
        isMobile
          ? !isTextBlock
            ? `flex ${
                isVertical ? "flex-col w-full" : "w-full mx-auto px-10"
              } items-center`
            : "hidden"
          : `flex ${
              isVertical
                ? "flex-col w-full justify-between 2xl:items-center 2xl:w-3/5 lg:justify-start"
                : "justify-center 2xl:pr-36"
            } items-center`
      }
      style={isMobile || isVertical ? { height: containerHeight } : {}}
    >
      <div className={isVertical ? "hidden" : "w-16"}>{leftArrowButton}</div>
      <div
        className={
          isMobile && isVertical ? "flex pb-4 gap-10 justify-center" : "hidden"
        }
      >
        <div className="flex w-16 justify-center">{leftArrowButton}</div>
        <div className="flex w-16 justify-center">{rightArrowButton}</div>
      </div>
      <div
        className={`flex gap-4 border-double ${
          isDark ? "border-gray-700" : " border-red-200"
        }  justify-center rounded-3xl ${isMobile ? "w-full" : `border-8 p-3`}`}
      >
        {images[photoNumber]?.map((url) => {
          const isDualMode =
            (isMobile && !isVertical) || (!isMobile && isVertical);

          return (
            <img
              key={url}
              className={`rounded-xl`}
              style={{ opacity: isDark ? 0.8 : 1 }}
              src={url}
              alt={username}
              width={
                isMobile
                  ? isDualMode
                    ? "20%"
                    : "80%"
                  : isDualMode
                  ? "50%"
                  : desktopWidht
              }
              onClick={() => {
                if (!isMobile || (isMobile && !isVertical)) {
                  setContent(
                    <img
                      className="rounded-xl"
                      src={url}
                      alt={username}
                      width={
                        isMobile
                          ? window.innerWidth * 0.5
                          : window.innerHeight * (isVertical ? 0.6 : 0.7)
                      }
                    />
                  );
                  setModalOpen(true);
                }
              }}
            />
          );
        })}
      </div>
      <div className={isVertical ? "hidden" : "w-16 pl-4"}>
        {rightArrowButton}
      </div>
      <div
        className={
          !isMobile && isVertical
            ? "flex pb-4 gap-10 pt-6 2xl:hidden"
            : "hidden"
        }
      >
        {leftArrowButton}
        {rightArrowButton}
      </div>
    </div>
  );
};

export default function PersonalThanks({
  isDark,
  isVertical,
  isMobile,
  setContent,
  setModalOpen,
  username,
}: PersonalProps) {
  const [isTextBlock, setTextBlock] = useState(true);

  const smallerSide = isVertical ? window.innerWidth : window.innerHeight;

  const isSmallScreen =
    window.visualViewport &&
    window.visualViewport.height < (isMobile ? 670 : 1536);

  const multiplier = isMobile
    ? isSmallScreen
      ? isVertical
        ? 0.75
        : 0.4
      : 0.9
    : isSmallScreen
    ? 0.4
    : 0.75;

  const containerHeight = ((smallerSide * multiplier) / 4) * 5;

  const desktopStyle = `${desktopBlockStyle} lg:pt-20 ${
    isVertical ? "flex flex-col relative items-center pt-6" : "mx-auto 2xl:p-20"
  } h-[90vh]`;

  const mobileStyle = `flex flex-col w-full ${
    window.innerWidth < 321 ? "pt-4" : isVertical ? "pt-8" : "pt-6"
  } items-center`;

  const imageBlock = (
    <ImageBlock
      isMobile={isMobile}
      isDark={isDark}
      isVertical={isVertical}
      username={username}
      setContent={setContent}
      setModalOpen={setModalOpen}
      isTextBlock={isTextBlock}
      containerHeight={containerHeight}
    />
  );

  return (
    <div className={isMobile ? mobileStyle : desktopStyle}>
      <p
        className={`${
          isMobile
            ? (isVertical && window.innerWidth < 321) ||
              (!isVertical && window.innerHeight < 321)
              ? "text-lg"
              : "text-2xl"
            : "text-3xl pl-2"
        } text-center`}
      >
        {names[username]}е
      </p>
      <div
        className={`flex ${
          isMobile
            ? `w-[90vw] ${
                isVertical
                  ? window.innerWidth < 321
                    ? "pt-4"
                    : "pt-6"
                  : "pt-4"
              }`
            : `${
                isVertical
                  ? "flex-col items-center gap-10 lg:px-10 pt-6 lg:pt-10 "
                  : "pt-20 gap-4"
              }`
        }`}
      >
        <div
          className={
            isMobile
              ? isTextBlock
                ? "flex"
                : "hidden"
              : `flex justify-center ${isVertical ? "justify-center" : "w-1/2"}`
          }
          style={isMobile ? { height: containerHeight } : {}}
        >
          <div
            className={`${isDark ? "bg-gray-900" : "bg-red-100"} ${
              isMobile
                ? `flex flex-col py-2 px-4 h-max ${
                    isSmallScreen
                      ? `py-4 ${
                          isVertical
                            ? window.innerWidth < 321
                              ? "text-xs"
                              : "text-sm"
                            : "text-xs"
                        }`
                      : ""
                  }`
                : `${!isVertical ? "w-4/5 sm:text-sm xl:text-lg" : "w-full"} `
            } p-8 rounded-xl`}
          >
            <p>
              Мы долго готовились к этому дню, но таким особенным его сделали
              наши гости.
            </p>
            <br />
            {thanks[username].map((sentence, index) => (
              <p key={sentence}>
                {sentence}
                {index < thanks[username].length - 1 && (
                  <>
                    <br />
                    <br />
                  </>
                )}
              </p>
            ))}
          </div>
        </div>
        {imageBlock}
      </div>
      <div className={isMobile ? (isVertical ? "pt-16" : "pt-6") : "hidden"}>
        <button onClick={() => setTextBlock(!isTextBlock)}>
          <img
            src={`/statistics/arrow-${isTextBlock ? "right" : "return"}-${
              isDark ? "dark" : "light"
            }.png`}
            alt="next"
            width={isVertical ? 45 : 40}
          />
        </button>
      </div>
      <div
        className={
          !isMobile && isVertical && !isSmallScreen
            ? "absolute bottom-0 left-0 px-20"
            : "hidden"
        }
        style={{ opacity: 0.7 }}
      >
        <img src={`/placeholder-${isDark ? "dark" : "light"}.png`} alt="" />
      </div>
    </div>
  );
}
